import React, { useState, useEffect, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import Select, { components } from "react-select";
import { useNavigate, useLocation } from "react-router-dom";
import Topbar from "../../components/Topbar/Topbar";
import { URLS } from "../../Services/Constant";
import { get, post, put } from "../../Services/ApiHandler";
import { toast } from "react-toastify";

const LOCATION_TYPES = {
  STATE: 2,
  CITY: 3,
  AREA: 4,
};

const CheckboxOption = (props) => {
  const isAllGenericActive = props.selectProps.isAllSelected;
  let isChecked = props.isSelected;
  if (isAllGenericActive) {
    isChecked = true;
  }
  return (
    <components.Option {...props}>
      <div style={{ display: "flex", alignItems: "center" }}>
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => null}
          style={{
            marginRight: "10px",
            cursor: "pointer",
            width: "16px",
            height: "16px",
            accentColor: "#A140E0",
          }}
        />
        <span>{props.label}</span>
      </div>
    </components.Option>
  );
};

const hideMultiValue = {
  multiValue: () => ({ display: "none" }),
  valueContainer: (provided) => ({
    ...provided,
    height: "55px",
    padding: "0 6px",
  }),
  control: (provided) => ({
    ...provided,
    minHeight: "55px",
    borderRadius: "8px",
    backgroundColor: "#F4F4F4",
    border: "none",
    boxShadow: "none",
    opacity: 1,
  }),
  indicatorSeparator: () => ({ display: "none" }),
};

const AddCreateTemplate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { type, data } = location.state || {};
  const isViewMode = type === "view";
  
  const [locationList, setLocationsList] = useState({
    states: [],
    cities: [],
  });
  
  const [serviceOptions, setServiceOptions] = useState([]);
  
  const notificationTypeOptions = [
    { value: 1, label: "Birth Date" },
    { value: 2, label: "Booked" },
    { value: 3, label: "Un Booked" },
    { value: 4, label: "Cancellation" },
  ];

  const getServicesList = useCallback(async () => {
    try {
      const res = await get(URLS.SERVICESLIST_DROPDOWN);
      if (res?.status === "success") {
        const result = res.data.map((item) => ({
          label: item.display_name,
          value: item.id,
        }));
        setServiceOptions(result);
      }
    } catch (error) {}
  }, []);

  useEffect(() => {
    getServicesList();
  }, [getServicesList]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      templateName: "",
      state: [],
      city: [],
      status: [],
      service: [],
      selectAllState: false,
      selectAllCity: false,
      selectAllStatus: false,
      selectAllService: false,
    },
  });

  const selectedState = watch("state");
  const isAllStatesActive = watch("selectAllState");
  const isAllCitiesActive = watch("selectAllCity");
  const isAllStatusActive = watch("selectAllStatus");
  const isAllServiceActive = watch("selectAllService");

  const getLocationsList = useCallback(async (locationType, parent_id) => {
    try {
      const url = `${URLS.LOCATION}?type=${locationType}${
        parent_id ? "&parent_id=" + parent_id : ""
      }`;
      const res = await get(url);

      if (res?.status === "success") {
        const result = res.data.map((item) => ({
          value: item.id,
          label: item.name,
        }));

        if (locationType === LOCATION_TYPES.STATE) {
          setLocationsList((prev) => ({ ...prev, states: result }));
        }
      }
    } catch (error) {}
  }, []);

  useEffect(() => {
    getLocationsList(LOCATION_TYPES.STATE);
  }, [getLocationsList]);

  useEffect(() => {
    if (data && locationList.states.length > 0 && serviceOptions.length > 0) {
      setValue("templateName", data.name || data.template_name || "");

      if (data.notification_type) {
        const typeOption = notificationTypeOptions.find(o => o.value === data.notification_type);
        if (typeOption) setValue("status", typeOption);
      }

      if (data.service) {
        const serviceId = typeof data.service === 'object' ? data.service.id : data.service;
        const sOption = serviceOptions.find(o => o.value === serviceId);
        if (sOption) setValue("service", sOption);
      }

      const statesArray = data.state_ids || data.states || [];
      if (statesArray.length > 0) {
        const stateIds = statesArray.map(s => typeof s === 'object' ? s.id : s);
        const mappedStates = locationList.states.filter(s => stateIds.includes(s.value));
        if (mappedStates.length === locationList.states.length && locationList.states.length > 0) {
          setValue("selectAllState", true);
          setValue("state", [{ value: "all", label: "All" }]);
        } else {
          setValue("state", mappedStates);
        }
      }
    }
  }, [data, locationList.states, serviceOptions, setValue]);

  useEffect(() => {
    if (data && locationList.cities.length > 0) {
      const citiesArray = data.city_ids || data.cities || [];
      if (citiesArray.length > 0) {
        const cityIds = citiesArray.map(c => typeof c === 'object' ? c.id : c);
        const mappedCities = locationList.cities.filter(c => cityIds.includes(c.value));
        if (mappedCities.length > 0) {
          if (mappedCities.length === locationList.cities.length) {
             setValue("selectAllCity", true);
             setValue("city", [{ value: "all", label: "All" }]);
          } else {
             setValue("city", mappedCities);
          }
        }
      }
    }
  }, [data, locationList.cities, setValue]);

  useEffect(() => {
    const isAllSelected = Array.isArray(selectedState)
      ? selectedState[0]?.value === "all"
      : selectedState?.value === "all";

    const hasStates = Array.isArray(selectedState)
      ? selectedState.length > 0
      : !!selectedState;

    if (hasStates) {
      const fetchCities = async () => {
        const payload = {
          state_ids: isAllSelected
            ? []
            : Array.isArray(selectedState)
              ? selectedState.map((s) => s.value)
              : [selectedState.value],
        };
        try {
          const res = await post(URLS.NOTIFICATION_LOCATION_CITIES_BY_STATES, payload);
          if (res?.status === "success") {
            const allCities = res.data.map((item) => ({
              value: item.id,
              label: item.name,
            }));
            setLocationsList((prev) => ({ ...prev, cities: allCities }));
          }
        } catch (error) {}
      };
      fetchCities();
    } else {
      setLocationsList((prev) => ({ ...prev, cities: [] }));
      setValue("city", []);
    }
  }, [selectedState, setValue]);

  const onSubmit = async (formData) => {
    if (isViewMode) return;
    
    const getStateIds = () => {
      if (!formData?.state?.length) return [];
      if (formData.state[0]?.value === "all") {
        return locationList?.states?.map((s) => s.value) || [];
      }
      return formData.state.map((s) => s.value);
    };

    const getCityIds = () => {
      if (!formData?.city?.length) return [];
      if (formData.city[0]?.value === "all") {
        return locationList?.cities?.map((c) => c.value) || [];
      }
      return formData.city.map((c) => c.value);
    };

    const getNotificationType = () => {
      if (!formData?.status) return null;
      if (Array.isArray(formData.status)) {
         return formData.status[0]?.value === "all" ? 0 : formData.status[0]?.value;
      }
      return formData.status.value === "all" ? 0 : formData.status.value;
    };

    const getServiceId = () => {
      if (!formData?.service) return null;
      if (Array.isArray(formData.service)) {
         return formData.service[0]?.value === "all" ? 0 : formData.service[0]?.value;
      }
      return formData.service.value === "all" ? 0 : formData.service.value;
    };

    const payload = {
      name: formData.templateName,
      notification_type: getNotificationType(),
      service: getServiceId(),
      state_ids: getStateIds(),
      city_ids: getCityIds()
    };

    try {
      const isEdit = !!data?.id;
      const url = isEdit ? `${URLS.USER_NOTIFICATION_TEMPLATE_EDIT}${data.id}/` : URLS.USER_NOTIFICATION_TEMPLATE_CREATE;
      const method = isEdit ? put : post;

      const res = await method(url, payload);
      if (res?.status === "success" || res?.status === 200 || res?.status === 201 || res?.name) {
        toast.success(res?.message || "Template Saved Successfully!");
        navigate("/create-template");
      } else {
        toast.error(res?.message || "Failed to save template");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "An error occurred while saving the template");
    }
  };

  return (
    <div className="layout_body p-3" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <div className="d-flex align-items-center justify-content-between header mb-3">
        <h2 className="mt-0 mb-0 ms-2 fw-bold" style={{ fontSize: "24px", color: "#000" }}>
          User Notification - Template
        </h2>
        <Topbar />
      </div>

      <div className="card-box mt-3 bg-white p-4 mx-2" style={{ borderRadius: "16px" }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Notification Template Name */}
          <div className="mb-4 w-100 d-block">
            <label className="f2 fs-16 mb-2 d-block fw-bold">
              Notification Template Name <span className="text-danger">*</span>
            </label>
            <div className="w-100 d-block">
              <Controller
                name="templateName"
                control={control}
                rules={{ required: "Template name is required" }}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    placeholder="Template Name"
                    className="form-control w-100"
                    style={{
                      backgroundColor: "#F4F4F4",
                      borderRadius: "8px",
                      height: "55px",
                      width: "100%",
                      minWidth: "100%",
                      padding: "0 16px",
                      border: errors.templateName ? "1px solid red" : "none",
                      outline: "none",
                      display: "block"
                    }}
                    readOnly={isViewMode}
                  />
                )}
              />
            </div>
            {errors.templateName && (
              <span className="text-danger fs-12">{errors.templateName.message}</span>
            )}
          </div>

          <div className="row mb-4">
            {/* State */}
            <div className="col-md-6 mb-3 mb-md-0">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="f2 fs-16 m-0 fw-bold">
                  State <span className="text-danger">*</span>
                </label>
                <div className="d-flex align-items-center">
                  <input
                    type="checkbox"
                    className="me-2"
                    style={{ width: "16px", height: "16px", accentColor: "#A140E0" }}
                    checked={isAllStatesActive}
                    disabled={isViewMode}
                    onChange={(e) => {
                      if (isViewMode) return;
                      setValue("selectAllState", e.target.checked);
                      setValue("state", e.target.checked ? [{ value: "all", label: "All" }] : []);
                    }}
                  />
                  <span className="fs-14 fw-bold">Select All</span>
                </div>
              </div>
              <Controller
                name="state"
                control={control}
                rules={{ required: "State is required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    isAllSelected={isAllStatesActive}
                    onChange={(val, meta) => {
                      if (meta.action === "select-option" && meta.option?.value === "all") {
                        setValue("selectAllState", true);
                        field.onChange([{ value: "all", label: "All" }]);
                        return;
                      }
                      if (meta.action === "clear" || !val || val.length === 0) {
                        setValue("selectAllState", false);
                        field.onChange([]);
                        return;
                      }
                      if (isViewMode) return;
                      setValue("selectAllState", false);
                      field.onChange(val);
                    }}
                    options={[{ value: "all", label: "All" }, ...locationList.states]}
                    isMulti
                    menuIsOpen={isViewMode ? false : undefined}
                    isSearchable={!isViewMode}
                    components={{ Option: CheckboxOption }}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "55px",
                        borderRadius: "8px",
                        backgroundColor: "#F4F4F4",
                        border: errors.state ? "1px solid red" : "none",
                        boxShadow: "none",
                      })
                    }}
                  />
                )}
              />
              {errors.state && (
                <span className="text-danger fs-12">{errors.state.message}</span>
              )}
            </div>

            {/* City */}
            <div className="col-md-6">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="f2 fs-16 m-0 fw-bold">
                  City <span className="text-danger">*</span>
                </label>
                <div className="d-flex align-items-center">
                  <input
                    type="checkbox"
                    className="me-2"
                    style={{ width: "16px", height: "16px", accentColor: "#A140E0" }}
                    checked={isAllCitiesActive}
                    disabled={isViewMode}
                    onChange={(e) => {
                      if (isViewMode) return;
                      setValue("selectAllCity", e.target.checked);
                      setValue("city", e.target.checked ? [{ value: "all", label: "All" }] : []);
                    }}
                  />
                  <span className="fs-14 fw-bold">Select All</span>
                </div>
              </div>
              <Controller
                name="city"
                control={control}
                rules={{ required: "City is required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    isAllSelected={isAllCitiesActive}
                    onChange={(val, meta) => {
                      if (meta.action === "select-option" && meta.option?.value === "all") {
                        setValue("selectAllCity", true);
                        field.onChange([{ value: "all", label: "All" }]);
                        return;
                      }
                      if (meta.action === "clear" || !val || val.length === 0) {
                        setValue("selectAllCity", false);
                        field.onChange([]);
                        return;
                      }
                      if (isViewMode) return;
                      setValue("selectAllCity", false);
                      field.onChange(val);
                    }}
                    options={[{ value: "all", label: "All" }, ...locationList.cities]}
                    isMulti
                    menuIsOpen={isViewMode ? false : undefined}
                    isSearchable={!isViewMode}
                    components={{ Option: CheckboxOption }}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "55px",
                        borderRadius: "8px",
                        backgroundColor: "#F4F4F4",
                        border: errors.city ? "1px solid red" : "none",
                        boxShadow: "none",
                      })
                    }}
                  />
                )}
              />
              {errors.city && (
                <span className="text-danger fs-12">{errors.city.message}</span>
              )}
            </div>
          </div>

          <div className="row mb-5">
            {/* Notification Type */}
            <div className="col-md-6 mb-3 mb-md-0">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="f2 fs-16 m-0 fw-bold">
                  Notification Type <span className="text-danger">*</span>
                </label>
                <div className="d-flex align-items-center">
                  <input
                    type="checkbox"
                    className="me-2"
                    style={{ width: "16px", height: "16px", accentColor: "#A140E0" }}
                    checked={isAllStatusActive}
                    disabled={isViewMode}
                    onChange={(e) => {
                      if (isViewMode) return;
                      setValue("selectAllStatus", e.target.checked);
                      setValue("status", e.target.checked ? { value: "all", label: "All" } : null);
                    }}
                  />
                  <span className="fs-14 fw-bold">Select All</span>
                </div>
              </div>
              <Controller
                name="status"
                control={control}
                rules={{ required: "Notification Type is required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    isAllSelected={isAllStatusActive}
                    onChange={(val, meta) => {
                      if (val?.value === "all") {
                        setValue("selectAllStatus", true);
                        field.onChange({ value: "all", label: "All" });
                        return;
                      }
                      if (meta.action === "clear" || !val) {
                        setValue("selectAllStatus", false);
                        field.onChange(null);
                        return;
                      }
                      if (isViewMode) return;
                      setValue("selectAllStatus", false);
                      field.onChange(val);
                    }}
                    options={[{ value: "all", label: "All" }, ...notificationTypeOptions]}
                    menuIsOpen={isViewMode ? false : undefined}
                    isSearchable={!isViewMode}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "55px",
                        borderRadius: "8px",
                        backgroundColor: "#F4F4F4",
                        border: errors.status ? "1px solid red" : "none",
                        boxShadow: "none",
                      })
                    }}
                  />
                )}
              />
              {errors.status && (
                <span className="text-danger fs-12">{errors.status.message}</span>
              )}
            </div>

            {/* Service Type */}
            <div className="col-md-6">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="f2 fs-16 m-0 fw-bold">
                  Service type <span className="text-danger">*</span>
                </label>
                <div className="d-flex align-items-center">
                  <input
                    type="checkbox"
                    className="me-2"
                    style={{ width: "16px", height: "16px", accentColor: "#A140E0" }}
                    checked={isAllServiceActive}
                    disabled={isViewMode}
                    onChange={(e) => {
                      if (isViewMode) return;
                      setValue("selectAllService", e.target.checked);
                      setValue("service", e.target.checked ? { value: "all", label: "All" } : null);
                    }}
                  />
                  <span className="fs-14 fw-bold">Select All</span>
                </div>
              </div>
              <Controller
                name="service"
                control={control}
                rules={{ required: "Service type is required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    isAllSelected={isAllServiceActive}
                    onChange={(val, meta) => {
                      if (val?.value === "all") {
                        setValue("selectAllService", true);
                        field.onChange({ value: "all", label: "All" });
                        return;
                      }
                      if (meta.action === "clear" || !val) {
                        setValue("selectAllService", false);
                        field.onChange(null);
                        return;
                      }
                      if (isViewMode) return;
                      setValue("selectAllService", false);
                      field.onChange(val);
                    }}
                    options={[{ value: "all", label: "All" }, ...serviceOptions]}
                    menuIsOpen={isViewMode ? false : undefined}
                    isSearchable={!isViewMode}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "55px",
                        borderRadius: "8px",
                        backgroundColor: "#F4F4F4",
                        border: errors.service ? "1px solid red" : "none",
                        boxShadow: "none",
                      })
                    }}
                  />
                )}
              />
              {errors.service && (
                <span className="text-danger fs-12">{errors.service.message}</span>
              )}
            </div>
          </div>

          <div className="d-flex justify-content-between mt-5 pt-3">
            <button
              type="button"
              className="btn px-5 py-2 fw-bold"
              style={{
                backgroundColor: "#ecd6ff",
                color: "#a140e0",
                borderRadius: "8px",
                minWidth: "150px"
              }}
              onClick={() => navigate("/create-template")}
            >
              {isViewMode ? "Back" : "Cancel"}
            </button>
            {!isViewMode && (
              <button
                type="submit"
                className="btn px-5 py-2 fw-bold"
                style={{
                  backgroundColor: "#a140e0",
                  color: "#fff",
                  borderRadius: "8px",
                  minWidth: "150px"
                }}
              >
                Save
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCreateTemplate;
